import { Link } from 'react-router-dom';
import { FiCheck, FiSmartphone, FiZap, FiGlobe, FiShoppingCart, FiBarChart2, FiGrid, FiUsers, FiCreditCard, FiDollarSign, FiDroplet, FiUploadCloud, FiPackage, FiMonitor, FiUserCheck, FiPrinter, FiMapPin, FiLink } from 'react-icons/fi';
import Seo from '../components/Seo';
import { featurePages, localizeFeature } from '../data/featurePages';
import { localizeUseCase, useCasePages } from '../data/useCasePages';
import { useSiteLanguage } from '../i18n/siteLanguage';
import styles from './Landing.module.css';

function Landing() {
  const { language } = useSiteLanguage();
  const isGreek = language === 'el';
  const localizePath = (path) => (isGreek ? (path === '/' ? '/el' : `/el${path}`) : path);
  const localizedFeaturePages = featurePages.map((feature) => localizeFeature(feature, language));
  const localizedUseCasePages = useCasePages.map((useCase) => localizeUseCase(useCase, language));

  const features = [
    {
      icon: <FiSmartphone />,
      title: 'QR Code Menus',
      description: 'Generate dynamic QR codes instantly. Customers scan and browse your full menu on their phone.',
    },
    {
      icon: <FiGrid />,
      title: 'Drag & Drop Builder',
      description: 'Organize categories and dishes visually. Add images, prices, allergens, modifiers, and category serving time windows.',
    },
    {
      icon: <FiShoppingCart />,
      title: 'Online Ordering',
      description: 'Accept orders with multiple payment options, partial payments, self pick-up, order day management, and live status tracking.',
    },
    {
      icon: <FiGlobe />,
      title: 'Multi-Language',
      description: 'Serve international guests with menus in 25+ languages. Auto-detect or let customers choose.',
    },
    {
      icon: <FiUsers />,
      title: 'Table & Waiter Calls',
      description: 'Assign QR codes per table, set ordering schedules, enable self pick-up, set minimum amounts, and let guests call staff.',
    },
    {
      icon: <FiBarChart2 />,
      title: 'Actionable Analytics',
      description: 'Track scans, views, orders, revenue, and conversion funnels. Get insights to optimize your menu.',
    },
    {
      icon: <FiCreditCard />,
      title: 'Payments & Billing',
      description: 'Accept payments via Stripe, Viva SoftPOS Terminal, cash, or room charge. Submit tax invoices to AADE.',
    },
    {
      icon: <FiDollarSign />,
      title: 'Pay at Table',
      description: 'Let customers pay the remaining balance online after confirmation — via a secure link on their phone.',
    },
    {
      icon: <FiMapPin />,
      title: 'Delivery Zones & Pick-up',
      description: 'Define delivery areas on a map, let customers choose delivery or self pick-up, verify addresses, and send estimated time notifications.',
    },
    {
      icon: <FiDroplet />,
      title: 'Custom Branding',
      description: 'Auto-detect colors from your logo, customize fonts, header icon colors, and border radius for a perfect brand match.',
    },
    {
      icon: <FiUploadCloud />,
      title: 'CSV Import & Export',
      description: 'Migrate menus in bulk with CSV import, export your data anytime, and download templates.',
    },
    {
      icon: <FiPackage />,
      title: 'Inventory Tracking',
      description: 'Track stock per dish with card-level +/- controls, auto-decrement on orders, and auto-restore on cancellations with live updates.',
    },
    {
      icon: <FiUsers />,
      title: 'Employee Management',
      description: 'Add staff, assign them to seating areas, track orders per employee, and reconcile daily reports.',
    },
    {
      icon: <FiMonitor />,
      title: 'Kitchen Display (KDS)',
      description: 'Real-time kitchen screens with station views, item checklists, urgency alerts, and expediter mode for tablets.',
    },
    {
      icon: <FiUserCheck />,
      title: 'Customer Management',
      description: 'Auto-created customer profiles from orders. Search, view order history, total spend, and pre-fill checkout.',
    },
    {
      icon: <FiPrinter />,
      title: 'Receipt & Order Printing',
      description: 'Print thermal receipts and order tickets via network printers or PrintNode cloud. Route prints per location and seating area.',
    },
    {
      icon: <FiLink />,
      title: '3rd-Party Integrations',
      description: 'Connect with marketplace platforms like efood and Wolt, sync order statuses, and manage outlet OPEN/CLOSED/CHECKIN state in real time.',
    },
    {
      icon: <FiZap />,
      title: 'Guided Onboarding',
      description: 'Go live in under 5 minutes with a step-by-step setup wizard for location, branding, and menu.',
    },
  ];
  const featureTranslationsEl = [
    { title: 'QR Code Μενού', description: 'Δημιουργήστε δυναμικά QR και οι πελάτες βλέπουν πάντα το πιο πρόσφατο μενού.' },
    { title: 'Drag & Drop Builder', description: 'Οργανώστε κατηγορίες και πιάτα οπτικά, με εικόνες, τιμές, αλλεργιογόνα και ωράρια σερβιρίσματος.' },
    { title: 'Online Παραγγελίες', description: 'Πολλαπλοί τρόποι πληρωμής, μερικές πληρωμές, self pick-up, διαχείριση ημέρας και live παρακολούθηση.' },
    { title: 'Πολυγλωσσικό', description: 'Υποστήριξη 25+ γλωσσών με εύκολη εναλλαγή από τον πελάτη.' },
    { title: 'Τραπέζια & Κλήση Σερβιτόρου', description: 'QR ανά τραπέζι, πρόγραμμα παραγγελιών, self pick-up, ελάχιστο ποσό και κλήση προσωπικού.' },
    { title: 'Actionable Analytics', description: 'Παρακολουθήστε scans, προβολές, παραγγελίες και conversions.' },
    { title: 'Πληρωμές & Τιμολόγηση', description: 'Stripe, Viva SoftPOS Terminal, μετρητά ή χρέωση δωματίου. Φορολογική τιμολόγηση AADE.' },
    { title: 'Πληρωμή στο Τραπέζι', description: 'Οι πελάτες πληρώνουν το υπόλοιπο online μετά την επιβεβαίωση — μέσω ασφαλούς συνδέσμου στο κινητό τους.' },
    { title: 'Ζώνες Delivery & Παραλαβή', description: 'Ορίστε περιοχές delivery στον χάρτη, αφήστε τους πελάτες να επιλέξουν delivery ή παραλαβή, και στείλτε εκτιμώμενο χρόνο ετοιμασίας.' },
    { title: 'Custom Branding', description: 'Αυτόματη ανίχνευση χρωμάτων από logo, custom fonts, χρώματα εικονιδίων και πλήρης εμφάνιση.' },
    { title: 'CSV Import & Export', description: 'Μαζική μεταφορά δεδομένων με import/export CSV.' },
    { title: 'Διαχείριση Αποθέματος', description: 'Παρακολούθηση στοκ ανά πιάτο με +/- controls στην κάρτα, αυτόματη μείωση σε παραγγελία και αυτόματη επαναφορά σε ακύρωση με live ενημέρωση.' },
    { title: 'Διαχείριση Προσωπικού', description: 'Ορίστε βάρδιες και παρακολουθήστε επίδοση ανά εργαζόμενο.' },
    { title: 'Kitchen Display (KDS)', description: 'Ζωντανές οθόνες κουζίνας με σταθμούς, checklists και alerts.' },
    { title: 'Διαχείριση Πελατών', description: 'Αυτόματα προφίλ πελατών, ιστορικό παραγγελιών και pre-fill checkout.' },
    { title: 'Εκτύπωση Αποδείξεων', description: 'Θερμική εκτύπωση αποδείξεων και tickets μέσω δικτυακών εκτυπωτών ή PrintNode cloud. Δρομολόγηση ανά τοποθεσία.' },
    { title: '3rd-Party Integrations', description: 'Σύνδεση με πλατφόρμες όπως efood και Wolt, συγχρονισμός status παραγγελιών και διαχείριση outlet OPEN/CLOSED/CHECKIN σε πραγματικό χρόνο.' },
    { title: 'Guided Onboarding', description: 'Ξεκινήστε γρήγορα με οδηγό setup βήμα-βήμα.' },
  ];
  const localizedFeatures = isGreek
    ? features.map((feature, index) => ({ ...feature, ...featureTranslationsEl[index] }))
    : features;

  const steps = [
    { number: '1', title: 'Create your account', description: 'Sign up free in under 30 seconds. No credit card needed.', detail: 'Choose the Free plan to start, upgrade anytime.' },
    { number: '2', title: 'Build your menu', description: 'Add categories, dishes, images, prices, and dietary info.', detail: 'Import from CSV or build from scratch with drag-and-drop.' },
    { number: '3', title: 'Generate & share QR', description: 'Download your QR code in PNG, SVG, or PDF format.', detail: 'Print table tents, stickers, or posters. Share the link directly.' },
    { number: '4', title: 'Receive orders', description: 'Customers scan, browse, and order. You manage from the dashboard.', detail: 'Track analytics, manage tables, and optimize your menu.' },
  ];
  const localizedSteps = isGreek
    ? [
        { number: '1', title: 'Δημιουργία λογαριασμού', description: 'Εγγραφή δωρεάν σε λιγότερο από 30 δευτερόλεπτα.', detail: 'Ξεκινήστε με Free plan και αναβαθμίστε όποτε θέλετε.' },
        { number: '2', title: 'Δημιουργία μενού', description: 'Προσθέστε κατηγορίες, πιάτα, εικόνες και τιμές.', detail: 'Import από CSV ή δημιουργία από την αρχή με drag-and-drop.' },
        { number: '3', title: 'Δημιουργία & κοινοποίηση QR', description: 'Κατεβάστε QR σε PNG, SVG ή PDF.', detail: 'Εκτυπώστε table tents, stickers ή posters και μοιραστείτε το link.' },
        { number: '4', title: 'Λήψη παραγγελιών', description: 'Οι πελάτες σαρώνουν και παραγγέλνουν, εσείς διαχειρίζεστε από dashboard.', detail: 'Παρακολουθήστε analytics, τραπέζια και βελτιστοποιήστε το μενού.' },
      ]
    : steps;

  const testimonials = [
    { name: 'Maria S.', role: 'Restaurant Owner, Athens', text: 'We replaced our paper menus in a day. Customers love scanning and ordering from their phones. Our waiter efficiency improved by 30%.', rating: 5 },
    { name: 'James T.', role: 'Beach Bar Manager, Mykonos', text: 'The multilingual support is a game-changer for our international tourists. Setup took 10 minutes and we haven\'t looked back.', rating: 5 },
    { name: 'Sophie L.', role: 'Cafe Owner, Paris', text: 'The analytics showed us which pastries to feature. Our daily specials sell out faster now. The drag-and-drop builder is incredibly intuitive.', rating: 5 },
  ];
  const localizedTestimonials = isGreek
    ? [
        { name: 'Μαρία Σ.', role: 'Ιδιοκτήτρια Εστιατορίου, Αθήνα', text: 'Αντικαταστήσαμε τα έντυπα μενού σε μία μέρα. Οι πελάτες το λάτρεψαν και η αποδοτικότητα βελτιώθηκε θεαματικά.', rating: 5 },
        { name: 'James T.', role: 'Beach Bar Manager, Μύκονος', text: 'Η υποστήριξη πολλών γλωσσών ήταν καθοριστική για τους τουρίστες μας. Το setup ήταν πολύ γρήγορο.', rating: 5 },
        { name: 'Sophie L.', role: 'Ιδιοκτήτρια Cafe, Παρίσι', text: 'Τα analytics μας έδειξαν τι να προωθήσουμε. Τα daily specials φεύγουν πιο γρήγορα από ποτέ.', rating: 5 },
      ]
    : testimonials;

  const demoDishes = [
    {
      name: 'Mediterranean Salad',
      description: 'Fresh greens, feta cheese, olives, and citrus vinaigrette.',
      price: '€8.50',
      image: '/images/demo/mediterranean-salad.webp',
    },
    {
      name: 'Grilled Salmon Plate',
      description: 'Char-grilled salmon, lemon butter, seasonal vegetables.',
      price: '€16.90',
      image: '/images/demo/grilled-salmon.webp',
    },
    {
      name: 'Signature Berry Mojito',
      description: 'Mint, berries, lime, and sparkling finish over ice.',
      price: '€7.20',
      image: '/images/demo/berry-mojito.webp',
    },
  ];
  const localizedDemoDishes = isGreek
    ? [
        { ...demoDishes[0], name: 'Μεσογειακή Σαλάτα', description: 'Φρέσκα λαχανικά, φέτα, ελιές και βινεγκρέτ εσπεριδοειδών.' },
        { ...demoDishes[1], name: 'Πιάτο Σολομού', description: 'Σολομός σχάρας, λεμονάτη σάλτσα βουτύρου και λαχανικά εποχής.' },
        { ...demoDishes[2], name: 'Berry Mojito Signature', description: 'Μέντα, berries, λάιμ και δροσερό τελείωμα με πάγο.' },
      ]
    : demoDishes;

  const faqItems = [
    {
      q: 'Can I create QR menus for multiple locations?',
      a: 'Yes. Manage multiple menus and locations, batch-generate QR assets, and keep every location updated in real time.',
    },
    {
      q: 'Does digitizedmenu.com support online ordering?',
      a: 'Yes. Enable in-app ordering with cart, quantity, size selection, table number, comments, scheduled times, and status workflow.',
    },
    {
      q: 'Can guests view the menu in multiple languages?',
      a: 'Yes. Menus support 25+ languages with a customer-facing language switcher and a dashboard for managing translations.',
    },
    {
      q: 'Do I need technical skills to set up?',
      a: 'Not at all. The guided onboarding wizard walks you through everything — location, branding, menu import, and QR generation. Most restaurants are live in under 5 minutes.',
    },
    {
      q: 'Can I import my menu with CSV and edit later?',
      a: 'Yes. You can bulk import categories and dishes via CSV, then quickly edit photos, prices, allergens, and translations from the dashboard.',
    },
    {
      q: 'Can I create guest Wi-Fi QR codes too?',
      a: 'Yes. You can configure Wi-Fi per seating area and download Wi-Fi QR codes from Settings, so guests can connect faster.',
    },
    {
      q: 'Can guests call a waiter from their phone?',
      a: 'Yes. Guests tap "Call Waiter" on the menu page. Staff receive instant notifications with acknowledge and resolve actions.',
    },
    {
      q: 'How do online payments work?',
      a: 'Connect your Stripe account through Stripe Connect onboarding. Customer payments go directly to your account. You can also configure waiter-confirm or hybrid payment flows per menu.',
    },
    {
      q: 'Can I customize the look of my digital menu?',
      a: 'Yes. Set custom colors, upload your logo, choose from 14 fonts, and adjust border radius. Each menu can have its own brand identity or inherit from location settings.',
    },
    {
      q: 'Can I manage employees and track their performance?',
      a: 'Yes. Create employee accounts, assign them to seating areas per shift, and track orders, revenue, payment methods, and average confirmation time per employee. Employees get a restricted dashboard showing only their assigned duties.',
    },
    {
      q: 'Is there a free plan?',
      a: 'Yes. The Free plan includes 1 menu with up to 10 dishes, QR codes, and multilingual support. Upgrade anytime for branding, ordering, analytics, payments, and more.',
    },
  ];
  const localizedFaqItems = isGreek
    ? [
        { q: 'Μπορώ να δημιουργήσω QR μενού για πολλαπλά locations;', a: 'Ναι. Διαχειρίζεστε πολλαπλά μενού και τοποθεσίες, με batch QR generation και live ενημερώσεις.' },
        { q: 'Υποστηρίζει online ordering;', a: 'Ναι. Υπάρχει καλάθι, ποσότητες, επιλογές μεγέθους, table number, σχόλια και status workflow.' },
        { q: 'Υποστηρίζονται πολλές γλώσσες;', a: 'Ναι. Το μενού υποστηρίζει 25+ γλώσσες με εύκολη εναλλαγή.' },
        { q: 'Χρειάζονται τεχνικές γνώσεις;', a: 'Όχι. Το guided onboarding σας καθοδηγεί βήμα-βήμα.' },
        { q: 'Μπορώ να κάνω CSV import το μενού και να το επεξεργαστώ μετά;', a: 'Ναι. Μπορείτε να περάσετε μαζικά κατηγορίες/πιάτα με CSV και μετά να κάνετε εύκολες διορθώσεις σε φωτογραφίες, τιμές, αλλεργιογόνα και μεταφράσεις.' },
        { q: 'Μπορώ να δημιουργήσω και Wi-Fi QR codes;', a: 'Ναι. Μπορείτε να ορίσετε Wi-Fi ανά seating area και να κατεβάσετε Wi-Fi QR codes από τα Settings.' },
        { q: 'Μπορεί ο πελάτης να καλέσει σερβιτόρο;', a: 'Ναι. Από τη σελίδα μενού ο πελάτης καλεί προσωπικό με ένα tap.' },
        { q: 'Πώς λειτουργούν οι online πληρωμές;', a: 'Συνδέετε τον πάροχο πληρωμών σας και οι πληρωμές επιβεβαιώνονται με ασφαλή webhook ροή.' },
        { q: 'Μπορώ να προσαρμόσω την εμφάνιση του μενού;', a: 'Ναι. Χρώματα, λογότυπο, fonts και εμφάνιση πλήρως παραμετροποιήσιμα.' },
        { q: 'Μπορώ να διαχειριστώ προσωπικό και αποδοτικότητα;', a: 'Ναι. Δημιουργείτε λογαριασμούς εργαζομένων, αναθέτετε περιοχές και βλέπετε αναφορές ανά βάρδια.' },
        { q: 'Υπάρχει δωρεάν πακέτο;', a: 'Ναι. Το Free plan περιλαμβάνει 1 μενού με έως 10 πιάτα και βασικές δυνατότητες.' },
      ]
    : faqItems;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'digitizedmenu.com - Digital Menu Platform',
    description: 'Create QR code menus, accept online orders, manage tables, publish multilingual content, and track analytics.',
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  };

  return (
    <div className={styles.landing}>
      <Seo
        title={isGreek
          ? 'digitizedmenu.com | QR Μενού, Online Ordering, Πληρωμές, Branding & Analytics'
          : 'digitizedmenu.com | QR Code Menus, Online Ordering, Payments, Branding & Analytics'}
        description={isGreek
          ? 'Το digitizedmenu.com βοηθά εστιατόρια, ξενοδοχεία, κομμωτήρια, spa και wellness επιχειρήσεις να δημιουργήσουν ψηφιακά μενού με QR, online ordering, πληρωμές, branding και analytics.'
          : 'digitizedmenu.com helps restaurants, hotels, salons, spas, and fitness studios create digital menus with QR codes, online ordering, Stripe payments, custom branding, table management, employee scheduling, kitchen display system, customer management, 25+ languages, inventory tracking, and actionable analytics.'}
        canonicalPath={isGreek ? '/el' : '/'}
        keywords="digital menu, qr code menu, online ordering system, table management, employee management, kitchen display system, kds, customer management, menu analytics, multilingual menu, stripe payments, custom branding, csv menu import, inventory tracking, salon menu, spa treatment menu, gym service catalogue"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={`container ${styles.heroContainer}`}>
          <div className={styles.heroText}>
            <span className={styles.badge}>{isGreek ? 'Ψηφιακό Μενού για Κάθε Επιχείρηση' : 'Digital Menu for Every Business'}</span>
            <h1 className={styles.heroTitle}>
              {isGreek ? 'Το μενού σας, αλλιώς.' : 'Your menu, reimagined.'}
              <br />
              <span className={styles.accent}>{isGreek ? 'Ψηφιακό και ισχυρό.' : 'Digital and powerful.'}</span>
            </h1>
            <p className={styles.heroSubtitle}>
              {isGreek
                ? 'Δημιουργήστε όμορφα QR μενού, δεχτείτε online παραγγελίες με πολλαπλούς τρόπους πληρωμής (Stripe, Viva SoftPOS Terminal, μετρητά ή χρέωση δωματίου), χωρίστε λογαριασμούς με μερικές πληρωμές, ενεργοποιήστε self pick-up, ορίστε ωράρια σερβιρίσματος ανά κατηγορία, αυτόματη ανίχνευση χρωμάτων branding, διαχειριστείτε ημέρα παραγγελιών, ρυθμίστε τραπέζια με πρόγραμμα και ελάχιστα ποσά, τρέξτε realtime οθόνη κουζίνας, παρακολουθήστε πελάτες, υποστηρίξτε 25+ γλώσσες, ελέγξτε αποθέματα και αποκτήστε analytics — όλα από μία πλατφόρμα. Ιδανικό για εστιατόρια, καφέ, μπαρ, ξενοδοχεία, beach bars, food trucks, κομμωτήρια, spas και studios. Ξεκινήστε σε λιγότερο από 5 λεπτά.'
                : 'Create beautiful QR code menus, accept online orders with multiple payment options (Stripe, Viva SoftPOS Terminal, cash, or room charge), split bills with partial payments, enable self pick-up, define category serving time windows, auto-detect brand colors, manage order day sessions, customize branding, manage tables with ordering schedules and minimum amounts, run a real-time kitchen display, track customers, support 25+ languages, track inventory, and get actionable analytics — all from one platform. Perfect for restaurants, cafes, bars, hotels, beach bars, food trucks, salons, spas, and fitness studios. Set up in under 5 minutes.'}
            </p>
            <div className={styles.heroCta}>
              <Link to={localizePath('/register')} className="btn btn-primary btn-lg">
                {isGreek ? 'Ξεκινήστε Δωρεάν' : 'Get Started Free'}
              </Link>
              <Link to={localizePath('/features')} className="btn btn-secondary btn-lg">
                {isGreek ? 'Δείτε Δυνατότητες' : 'Explore Features'}
              </Link>
            </div>
            <div className={styles.heroChecks}>
              <span><FiCheck /> {isGreek ? 'Διαθέσιμο δωρεάν πακέτο' : 'Free plan available'}</span>
              <span><FiCheck /> {isGreek ? 'Χωρίς πιστωτική κάρτα' : 'No credit card required'}</span>
              <span><FiCheck /> {isGreek ? 'Setup σε 5 λεπτά' : 'Setup in 5 minutes'}</span>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.heroPhone}>
              <div className={styles.heroPhoneBar}>
                <span className={styles.heroPhoneDot} />
                <span className={styles.heroPhoneDot} />
                <span className={styles.heroPhoneDot} />
              </div>
              <div className={styles.heroPhoneScreen}>
                {localizedDemoDishes.slice(0, 2).map((dish) => (
                  <div key={dish.name} className={styles.heroPhoneDish}>
                    <img src={dish.image} alt={dish.name} width={56} height={56} loading="lazy" />
                    <div>
                      <strong>{dish.name}</strong>
                      <span>{dish.price}</span>
                    </div>
                  </div>
                ))}
                <div className={styles.heroPhoneAction}>
                  <span>🛒 {isGreek ? 'Προβολή Καλαθιού (2)' : 'View Cart (2)'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Highlights */}
      <section className={styles.statsStrip}>
        <div className={`container ${styles.statsGrid}`}>
          <div className={styles.stat}>
            <strong>17+</strong>
            <span>{isGreek ? 'Δυνατότητες' : 'Features'}</span>
          </div>
          <div className={styles.stat}>
            <strong>25+</strong>
            <span>{isGreek ? 'Γλώσσες' : 'Languages'}</span>
          </div>
          <div className={styles.stat}>
            <strong>&lt;5 min</strong>
            <span>{isGreek ? 'Χρόνος Εγκατάστασης' : 'Setup Time'}</span>
          </div>
          <div className={styles.stat}>
            <strong>{isGreek ? 'Δωρεάν' : 'Free'}</strong>
            <span>{isGreek ? 'Πακέτο Εκκίνησης' : 'Starter Plan'}</span>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className={styles.features} id="features">
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Δυνατότητες' : 'Features'}</span>
            <h2>{isGreek ? 'Όλα όσα χρειάζεστε για να γίνετε digital' : 'Everything you need to go digital'}</h2>
            <p>{isGreek ? 'Μια ολοκληρωμένη πλατφόρμα για σύγχρονες επιχειρήσεις εστίασης.' : 'A complete platform for modern food and beverage businesses. No technical skills required.'}</p>
          </div>
          <div className={styles.featureGrid}>
            {localizedFeatures.map((feature) => (
              <div key={feature.title} className={styles.featureCard}>
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
          <div className={styles.sectionCta}>
            <Link to={localizePath('/features')} className="btn btn-outline">
              {isGreek ? 'Δείτε Όλες τις Δυνατότητες Αναλυτικά' : 'Explore All Features in Detail'}
            </Link>
          </div>
        </div>
      </section>

      {/* Demo Menu Preview */}
      <section className={styles.demoSection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Live Προεπισκόπηση' : 'Live Preview'}</span>
            <h2>{isGreek ? 'Δείτε τι βλέπουν οι πελάτες σας' : 'See what your customers will see'}</h2>
            <p>{isGreek ? 'Πραγματική προεπισκόπηση μενού όπως εμφανίζεται στο κινητό μετά από σάρωση QR.' : 'A real menu preview showing exactly how dishes appear on mobile when customers scan your QR code.'}</p>
          </div>

          <div className={styles.demoLayout}>
            <div className={styles.demoPhone}>
              <div className={styles.demoPhoneNotch} />
              <div className={styles.demoPhoneHeader}>
                <strong>{isGreek ? 'Seaside Κύριο Μενού' : 'Seaside Main Menu'}</strong>
                <span className={styles.demoLiveBadge}>{isGreek ? 'Live' : 'Live'}</span>
              </div>
              <div className={styles.demoDishList}>
                {localizedDemoDishes.map((dish) => (
                  <article key={dish.name} className={styles.demoDish}>
                    <img src={dish.image} alt={dish.name} width={72} height={72} loading="lazy" />
                    <div className={styles.demoDishInfo}>
                      <h3>{dish.name}</h3>
                      <p>{dish.description}</p>
                    </div>
                    <span className={styles.demoDishPrice}>{dish.price}</span>
                  </article>
                ))}
              </div>
              <div className={styles.demoPhoneBottom}>
                <span>🙋 {isGreek ? 'Καλέστε Σερβιτόρο' : 'Call Waiter'}</span>
                <span>🛒 {isGreek ? 'Καλάθι' : 'Cart'}</span>
              </div>
            </div>

            <aside className={styles.demoInfo}>
              <h3>{isGreek ? 'Δοκιμάστε το διαδραστικό demo' : 'Try the interactive demo'}</h3>
              <p>
                {isGreek
                  ? 'Ανοίξτε το πλήρες demo για περιήγηση κατηγοριών, λεπτομέρειες πιάτων, επιλογές μεγεθών, flow παραγγελίας και call waiter.'
                  : 'Open the full demo page to test category browsing, dish details, size selection, ordering flow, and waiter call functionality.'}
              </p>
              <ul className={styles.demoFeatures}>
                <li><FiCheck /> {isGreek ? 'Περιήγηση κατηγοριών και πιάτων' : 'Browse categories and dishes'}</li>
                <li><FiCheck /> {isGreek ? 'Επιλογή μεγεθών και προσθήκη στο καλάθι' : 'Select sizes and add to cart'}</li>
                <li><FiCheck /> {isGreek ? 'Δοκιμαστική παραγγελία με σχόλια' : 'Place a test order with comments'}</li>
                <li><FiCheck /> {isGreek ? 'Εναλλαγή γλώσσας στο μενού' : 'Switch languages on the menu'}</li>
                <li><FiCheck /> {isGreek ? 'Κλήση σερβιτόρου από το τραπέζι' : 'Call a waiter from your table'}</li>
              </ul>
              <div className={styles.demoActions}>
                <a href="/m/infinity-view-menu?table=3&lang=en&qr=1" className="btn btn-primary btn-lg">
                  {isGreek ? 'Άνοιγμα Live Demo Μενού' : 'Open Live Demo Menu'}
                </a>
                <p className={styles.demoHint}>
                  {isGreek ? 'Το demo είναι πλήρως λειτουργικό με δείγματα δεδομένων.' : 'This demo menu is fully functional with sample data.'}
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={styles.howItWorks}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Ξεκινήστε' : 'Getting Started'}</span>
            <h2>{isGreek ? 'Live σε 4 απλά βήματα' : 'Live in 4 simple steps'}</h2>
            <p>{isGreek ? 'Από την εγγραφή μέχρι την πρώτη σάρωση πελάτη σε λιγότερο από 5 λεπτά.' : 'From signup to your first customer scan in under 5 minutes.'}</p>
          </div>
          <div className={styles.stepsGrid}>
            {localizedSteps.map((step, i) => (
              <div key={step.number} className={styles.step}>
                <div className={styles.stepNumber}>{step.number}</div>
                {i < steps.length - 1 && <div className={styles.stepConnector} />}
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                <span className={styles.stepDetail}>{step.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className={styles.useCasesSection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Κλάδοι' : 'Use Cases'}</span>
            <h2>{isGreek ? 'Σχεδιασμένο για κάθε επιχείρηση υπηρεσιών' : 'Built for every service business'}</h2>
            <p>{isGreek ? 'Από beach bars μέχρι ξενοδοχεία και spa, το digitizedmenu.com προσαρμόζεται στη ροή σας.' : 'From beachside bars to five-star hotels, salons to spas — digitizedmenu.com adapts to your service model.'}</p>
          </div>
          <div className={styles.useCaseGrid}>
            {localizedUseCasePages.map((uc) => (
              <Link key={uc.slug} to={localizePath(`/use-cases/${uc.slug}`)} className={styles.useCaseCard}>
                <span className={styles.useCaseIcon}>{uc.icon}</span>
                <h3>{isGreek ? uc.title : uc.title.replace('Digital Menus for ', '')}</h3>
                <p>{uc.shortDescription}</p>
              </Link>
            ))}
          </div>
          <div className={styles.sectionCta}>
            <Link to={localizePath('/use-cases')} className="btn btn-outline">{isGreek ? 'Δείτε Όλους τους Κλάδους' : 'View All Use Cases'}</Link>
          </div>
        </div>
      </section>

      {/* Feature Detail Links */}
      <section className={styles.featureLinks}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Αναλυτικά' : 'Deep Dive'}</span>
            <h2>{isGreek ? 'Εξερευνήστε κάθε δυνατότητα αναλυτικά' : 'Explore each feature in detail'}</h2>
            <p>{isGreek ? 'Αναλυτικές σελίδες που δείχνουν πώς λειτουργεί κάθε δυνατότητα στην πράξη.' : 'Dedicated pages showing exactly how each capability works for your business.'}</p>
          </div>
          <div className={styles.linkGrid}>
            {localizedFeaturePages.map((feature) => (
              <Link key={feature.slug} to={localizePath(`/features/${feature.slug}`)} className={styles.linkCard}>
                <span className={styles.linkCardIcon}>{feature.icon}</span>
                <div>
                  <h3>{feature.title}</h3>
                  <p>{feature.shortDescription}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={styles.testimonials}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Μαρτυρίες' : 'Testimonials'}</span>
            <h2>{isGreek ? 'Το εμπιστεύονται επιχειρήσεις παγκοσμίως' : 'Loved by business owners worldwide'}</h2>
            <p>{isGreek ? 'Δείτε τι λένε οι επιχειρήσεις που πέρασαν σε ψηφιακό μενού.' : 'See what businesses say about switching to digital menus with digitizedmenu.com.'}</p>
          </div>
          <div className={styles.testimonialGrid}>
            {localizedTestimonials.map((t) => (
              <article key={t.name} className={styles.testimonialCard}>
                <div className={styles.testimonialStars}>
                  {'★'.repeat(t.rating)}
                </div>
                <blockquote>"{t.text}"</blockquote>
                <div className={styles.testimonialAuthor}>
                  <div className={styles.testimonialAvatar}>{t.name[0]}</div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.faq} id="faq">
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{isGreek ? 'Συχνές Ερωτήσεις' : 'FAQ'}</span>
            <h2>{isGreek ? 'Συχνές Ερωτήσεις' : 'Frequently asked questions'}</h2>
            <p>{isGreek ? 'Όλα όσα χρειάζεστε πριν λανσάρετε το ψηφιακό σας μενού.' : 'Everything you need to know before launching your digital menu.'}</p>
          </div>
          <div className={styles.faqGrid}>
            {localizedFaqItems.map((item) => (
              <article key={item.q} className={styles.faqItem}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
          <div className={styles.sectionCta}>
            <Link to={localizePath('/faq')} className="btn btn-outline">{isGreek ? 'Δείτε Όλο το FAQ' : 'View Full FAQ'}</Link>
          </div>
        </div>
      </section>

      {/* Custom Development */}
      <section className={styles.customDev}>
        <div className="container">
          <div className={styles.customDevBox}>
            <div className={styles.customDevContent}>
              <span className={styles.customDevLabel}>{isGreek ? 'Προσαρμοσμένη Ανάπτυξη' : 'Custom Development'}</span>
              <h2>{isGreek ? 'Χρειάζεστε κάτι μοναδικό για την επιχείρησή σας;' : 'Need something unique for your business?'}</h2>
              <p>
                {isGreek
                  ? 'Η πλατφόρμα μας είναι πλήρως επεκτάσιμη. Αν η επιχείρησή σας χρειάζεται custom λειτουργίες, ειδικές ενσωματώσεις ή προσαρμογές που δεν καλύπτονται από τα τυπικά πακέτα, η ομάδα ανάπτυξης μπορεί να τις υλοποιήσει για εσάς.'
                  : 'Our platform is fully extensible. If your business requires custom features, specialized integrations, or tailored workflows not covered by the standard plans, our development team can build them for you.'}
              </p>
              <ul className={styles.customDevList}>
                <li><FiCheck /> {isGreek ? 'Custom ενσωματώσεις με POS, ERP ή CRM συστήματα' : 'Custom integrations with POS, ERP, or CRM systems'}</li>
                <li><FiCheck /> {isGreek ? 'Ειδικές ροές παραγγελιών και πληρωμών' : 'Specialized order and payment workflows'}</li>
                <li><FiCheck /> {isGreek ? 'Προσαρμοσμένα dashboards και αναφορές' : 'Tailored dashboards and reporting'}</li>
                <li><FiCheck /> {isGreek ? 'Σύνδεση με τρίτα APIs και υπηρεσίες' : 'Third-party API connections and services'}</li>
                <li><FiCheck /> {isGreek ? 'White-label λύσεις και custom branding' : 'White-label solutions and custom branding'}</li>
              </ul>
              <div className={styles.customDevCta}>
                <Link to={localizePath('/contact')} className="btn btn-primary btn-lg">
                  {isGreek ? 'Επικοινωνήστε Μαζί Μας' : 'Contact Us'}
                </Link>
                <span className={styles.customDevNote}>
                  {isGreek ? 'Λάβετε προσφορά εντός 24 ωρών' : 'Get a quote within 24 hours'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.cta}>
        <div className="container">
          <div className={styles.ctaBox}>
            <h2>{isGreek ? 'Έτοιμοι να ψηφιοποιήσετε το μενού σας;' : 'Ready to digitize your menu?'}</h2>
            <p>{isGreek ? 'Ξεκινήστε δωρεάν και ανακαλύψτε πώς μπορεί να βοηθήσει την επιχείρησή σας.' : 'Start free and discover how digitizedmenu.com can help your business serve customers better.'}</p>
            <div className={styles.ctaButtons}>
              <Link to={localizePath('/register')} className="btn btn-primary btn-lg">
                {isGreek ? 'Ξεκινήστε Δωρεάν' : 'Start for Free'}
              </Link>
              <Link to={localizePath('/contact')} className="btn btn-secondary btn-lg">
                {isGreek ? 'Μιλήστε με Πωλήσεις' : 'Talk to Sales'}
              </Link>
            </div>
            <ul className={styles.ctaChecks}>
              <li><FiCheck /> {isGreek ? 'Χωρίς πιστωτική κάρτα' : 'No credit card required'}</li>
              <li><FiCheck /> {isGreek ? 'Δωρεάν πακέτο με 1 μενού' : 'Free plan with 1 menu'}</li>
              <li><FiCheck /> {isGreek ? 'Setup σε λιγότερο από 5 λεπτά' : 'Setup in under 5 minutes'}</li>
              <li><FiCheck /> {isGreek ? 'Ακύρωση οποιαδήποτε στιγμή' : 'Cancel anytime'}</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Landing;
